import React, { useState } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@store/authStore';
import { api } from '@services/api';
import toast from 'react-hot-toast';

interface ContactCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  dataSourceId: string;
  lists: any[];
  defaultListId?: string;
}

export const ContactCreateModal: React.FC<ContactCreateModalProps> = ({
  isOpen,
  onClose,
  companyId,
  dataSourceId,
  lists,
  defaultListId,
}) => {
  const { accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [campaignId, setCampaignId] = useState(defaultListId || '');
  const [tags, setTags] = useState('');
  const [subscribed, setSubscribed] = useState(true); // Default: subscribed
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState<'' | 'male' | 'female' | 'other'>('');

  const createContactMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/api/v1/getresponse/contacts', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('✅ Contact created successfully!');
      queryClient.invalidateQueries({ queryKey: ['getresponse-contacts'] });
      onClose();
      // Reset form
      setEmail('');
      setName('');
      setCampaignId(defaultListId || '');
      setTags('');
      setSubscribed(true); // Reset to default
      setPhone('');
      setDateOfBirth('');
      setGender('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create contact');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }

    if (!campaignId) {
      toast.error('Please select a list');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    createContactMutation.mutate({
      companyId,
      dataSourceId,
      email: email.trim(),
      name: name.trim() || undefined,
      campaignId,
      tags: tags.trim() ? tags.split(',').map(t => t.trim()) : [],
      subscribed, // Pass subscription status
      phone: phone.trim() || undefined,
      dateOfBirth: dateOfBirth || undefined,
      gender: gender || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create Contact
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Add a new contact to GetResponse
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={createContactMutation.isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Email (Required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contact@example.com"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              autoFocus
              disabled={createContactMutation.isPending}
            />
          </div>

          {/* Name (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={createContactMutation.isPending}
            />
          </div>

          {/* Phone (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1234567890"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={createContactMutation.isPending}
            />
          </div>

          {/* Date of Birth & Gender (Two columns) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                disabled={createContactMutation.isPending}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gender
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value as '' | 'male' | 'female' | 'other')}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                disabled={createContactMutation.isPending}
              >
                <option value="">Not specified</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* List Selection (Required) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              List <span className="text-red-500">*</span>
            </label>
            <select
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              disabled={createContactMutation.isPending}
            >
              <option value="">Select a list...</option>
              {lists.map((list) => (
                <option key={list.campaignId} value={list.campaignId}>
                  {list.name} ({list.contactCount || 0} contacts)
                </option>
              ))}
            </select>
          </div>

          {/* Email Subscription Status */}
          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={subscribed}
                onChange={(e) => setSubscribed(e.target.checked)}
                disabled={createContactMutation.isPending}
                className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer disabled:opacity-50"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Subscribe to email campaigns
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    subscribed 
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                  }`}>
                    {subscribed ? 'Opted In' : 'Opted Out'}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {subscribed 
                    ? '✅ Contact will receive automated emails and campaigns' 
                    : '⚠️ Contact will NOT receive emails (GDPR compliant)'}
                </p>
              </div>
            </label>
          </div>

          {/* Tags (Disabled for now) */}
          <div className="opacity-50">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tags <span className="text-xs text-yellow-600">(Coming Soon)</span>
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Tags require setup in GetResponse first"
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-500 cursor-not-allowed"
              disabled
            />
            <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
              ⚠️ Tags must be created in GetResponse first before assigning to contacts
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={createContactMutation.isPending}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createContactMutation.isPending || !email.trim() || !campaignId}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {createContactMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Create Contact
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

